import React from "react";
import { useRoutes } from "react-router-dom";
import type { IndexRouteObject, RouteObject } from "react-router-dom";

import { getValue, isNil } from "../utils";
import { RouteBox } from "../styles/common/style";
import {
  Module,
  NonIndexRouteWithFullPath,
  SubModule,
} from "../models/Modules";
import { UserModuleResponse } from "../models/Api/Modules";
import ErrorPage from "../components/errorPages/404";
import routeMapping, { Layout } from "./routePath";
import AuthGuard from "../components/auth/AuthGuard";
import Logout from "../components/Logout";
import { RouteSegments } from "../constants/ApplicationUrl";
import { prepareMenuItems, updateRouteObjectFullRoutePath } from "../utils/authentication/authorization";

export interface RouteProps {
  moduleData: UserModuleResponse;
}

const collectKnownRoutePatterns = (
  routeObjects: NonIndexRouteWithFullPath[]
): string[] => {
  const routePatterns: string[] = [];

  const visitRoute = (routeObject: NonIndexRouteWithFullPath) => {
    if (routeObject.fullRoutePath) {
      routePatterns.push(routeObject.fullRoutePath);
    }

    (routeObject.children || []).forEach((childRoute) => {
      visitRoute(childRoute);
    });
  };

  routeObjects.forEach((routeObject) => {
    visitRoute(routeObject);
  });

  return routePatterns;
};

const RoutesList: React.FC<RouteProps> = ({ moduleData }) => {
  const userModule = moduleData.response;
  const prepareAuthRoute = (
    userModuleResponse: Module[],
    routeMapping: NonIndexRouteWithFullPath[]
  ): RouteObject[] => {
    const routeMapObject: NonIndexRouteWithFullPath[] = [];
    const menuDataObject = prepareMenuItems(userModuleResponse);
    const routeMappingObjectfullPath =
      updateRouteObjectFullRoutePath(routeMapping);
    const knownRoutePatterns = collectKnownRoutePatterns(routeMappingObjectfullPath);

    const processRouteObject = (
      mapModuleItemCopy: NonIndexRouteWithFullPath,
      menuDataObjectIndex: number
    ) => {
      const isAuthenticated = menuDataObjectIndex > -1;
      mapModuleItemCopy.element = (
        <AuthGuard
          authenticated={isAuthenticated}
          userModuleResponse={userModuleResponse}
          requiredRoutePattern={mapModuleItemCopy.fullRoutePath}
          knownRoutePatterns={knownRoutePatterns}
        >
          {mapModuleItemCopy.element}
        </AuthGuard>
      );
      mapModuleItemCopy.fullRoutePath = isAuthenticated
        ? menuDataObject[menuDataObjectIndex].routeUrl
        : mapModuleItemCopy.fullRoutePath;
      mapModuleItemCopy.children = [];
    };

    const updateChildren = (
      parentChildren: NonIndexRouteWithFullPath[],
      childRoute: RouteObject,
      menuModule: Module | SubModule
    ): void => {
      const childCopy: NonIndexRouteWithFullPath = { ...childRoute } as NonIndexRouteWithFullPath;
      const subModules = menuModule?.subModules || [];
      const subModuleIndex = subModules.findIndex(
        (x: Module | SubModule) => x.routeUrl === childCopy.path
      );

      const isAuthenticated = subModuleIndex > -1;
      childCopy.element = (
        <AuthGuard
          authenticated={isAuthenticated}
          userModuleResponse={userModuleResponse}
          requiredRoutePattern={childCopy.fullRoutePath}
          knownRoutePatterns={knownRoutePatterns}
        >
          {childCopy.element}
        </AuthGuard>
      );
      childCopy.fullRoutePath = isAuthenticated
        ? subModules[subModuleIndex]?.fullRoutePath ?? undefined
        : childCopy.fullRoutePath;

      if (childRoute.children) {
        childCopy.children = [];
        childRoute.children.forEach((nestedChild: RouteObject) =>
          updateChildren(
            childCopy.children as NonIndexRouteWithFullPath[],
            nestedChild,
            subModules[subModuleIndex] || {}
          )
        );
      }

      parentChildren.push(childCopy);
    };

    routeMappingObjectfullPath.forEach((mapModuleItem: NonIndexRouteWithFullPath) => {
      const mapModuleItemCopy: NonIndexRouteWithFullPath = { ...mapModuleItem };
      const menuDataObjectIndex = menuDataObject.findIndex(
        (x) => x.routeUrl === mapModuleItemCopy.path
      );

      processRouteObject(mapModuleItemCopy, menuDataObjectIndex);

      mapModuleItem.children?.forEach((childRoute: RouteObject) =>
        updateChildren(
          mapModuleItemCopy.children as NonIndexRouteWithFullPath[],
          childRoute,
          menuDataObject[menuDataObjectIndex]
        )
      );

      routeMapObject.push(mapModuleItemCopy);
    });

    return convertToSingleLevelRoute(
      routeMapObject,
      userModuleResponse,
      knownRoutePatterns
    );
  };

  const convertToSingleLevelRoute = (
    routeMapObject: NonIndexRouteWithFullPath[],
    userModuleResponse: Module[],
    knownRoutePatterns: string[]
  ): RouteObject[] => {

    const hasModules = Array.isArray(userModuleResponse) && userModuleResponse.length > 0;

    const rootModuleId = Number(
      getValue(userModuleResponse, "[0].moduleId", -1)
    );

    const isModuleResolved = hasModules && rootModuleId !== -1;

const singleLevelroutes: RouteObject[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: isModuleResolved ? <ErrorPage /> : <></>,
      } as IndexRouteObject,
      {
        path: RouteSegments.LOGOUT,
        element: <Logout />,
      },
      {
        path: "*",
        element: (
          <AuthGuard
            authenticated={false}
            userModuleResponse={userModuleResponse}
            knownRoutePatterns={knownRoutePatterns}
            fallbackElement={<ErrorPage />}
          >
            <ErrorPage />
          </AuthGuard>
        ),
      },
    ],
  },
];

if (
  routeMapObject &&
  routeMapObject.length !== 0 &&
  !isNil(userModuleResponse)
) {
  const rootUrlElementObject = routeMapObject.find(
    (routeObject: NonIndexRouteWithFullPath) =>
      routeObject.path === getValue(userModuleResponse, "[0].routeUrl", null)
  );

  (singleLevelroutes[0].children?.[0] as IndexRouteObject).element = getValue(
    rootUrlElementObject,
    "element",
    (singleLevelroutes[0].children?.[0] as IndexRouteObject).element
  );
}

    const pushRouteElement = (
      pageRoutesNested: RouteObject[],
      routeObj: NonIndexRouteWithFullPath
    ) => {
      const { fullRoutePath, element, children } = routeObj;
      pageRoutesNested.push({
        path: fullRoutePath,
        element: element,
      });
      if (children && children.length > 0) {
        for (const childRouteObj of children) {
          pushRouteElement(pageRoutesNested, childRouteObj);
        }
      }
    };
    for (const routeObj of routeMapObject) {
      pushRouteElement(singleLevelroutes[0].children as RouteObject[], routeObj);
    }
    return singleLevelroutes;
  };

  const dynamicRouteData = prepareAuthRoute(userModule, routeMapping);
  const routeElements = useRoutes(dynamicRouteData);

  return (
    <React.Fragment>
      <RouteBox>
        {routeElements}
      </RouteBox>
    </React.Fragment>
  );
};

export default RoutesList;